import React, { useEffect, useState } from 'react';
import api from '../lib/api';

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-script';
const GMAPS_KEY_CACHE = 'google_maps_api_key_cache';

/**
 * GoogleMapsProvider - Loads Google Maps JavaScript API
 * Tries multiple sources for the API key:
 * 1. REACT_APP_GOOGLE_MAPS_API_KEY env var (build-time)
 * 2. LocalStorage cache (from previous successful fetch)
 * 3. Backend /settings/public/branding endpoint (with retry)
 */
export function GoogleMapsProvider({ children }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Already loaded globally
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsLoaded(true);
      return;
    }

    const loadScript = (apiKey) => {
      if (!apiKey) {
        setError('no_api_key');
        return;
      }

      // Cache the key for next page load
      try { localStorage.setItem(GMAPS_KEY_CACHE, apiKey); } catch {}

      // Script already injected
      const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
      if (existing) {
        if (window.google?.maps) {
          ensurePlacesLibrary().then(() => setIsLoaded(true)).catch(() => setError('load_failed'));
        } else {
          existing.addEventListener('load', () => {
            ensurePlacesLibrary().then(() => setIsLoaded(true)).catch(() => setError('load_failed'));
          });
          existing.addEventListener('error', () => setError('load_failed'));
        }
        return;
      }

      const script = document.createElement('script');
      script.id = GOOGLE_MAPS_SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=fr`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        ensurePlacesLibrary().then(() => {
          // GoogleMaps loaded
          setIsLoaded(true);
        }).catch(() => setError('load_failed'));
      };
      script.onerror = () => {
        setError('load_failed');
      };
      document.head.appendChild(script);
    };

    // Strategy 1: env var
    const envKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (envKey) {
      loadScript(envKey);
      return;
    }

    // Strategy 2: cached key from localStorage (instant, no network)
    const cachedKey = localStorage.getItem(GMAPS_KEY_CACHE);
    if (cachedKey) {
      loadScript(cachedKey);
      // Also refresh from backend in background to keep cache fresh
      fetchKeyFromBackend().then(key => {
        if (key && key !== cachedKey) {
          localStorage.setItem(GMAPS_KEY_CACHE, key);
        }
      }).catch(() => {});
      return;
    }

    // Strategy 3: fetch from backend with retry
    fetchKeyWithRetry(3, 2000)
      .then(key => loadScript(key))
      .catch(() => setError('no_api_key'));
  }, []);

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, error }}>
      {children}
    </GoogleMapsContext.Provider>
  );
}

async function fetchKeyFromBackend() {
  try {
    const res = await api.get('/settings/public/branding', { _skipAuthRedirect: true });
    return res.data?.google_maps_api_key || '';
  } catch {
    return '';
  }
}

async function fetchKeyWithRetry(maxRetries, delay) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const key = await fetchKeyFromBackend();
    if (key) {
      // Key fetched
      return key;
    }
    if (attempt < maxRetries) {
      await new Promise(r => setTimeout(r, delay));
    }
  }
  return '';
}

/**
 * Ensure the Places library is available.
 * Modern Google Maps API (v3.56+) may require importLibrary('places').
 */
async function ensurePlacesLibrary() {
  if (window.google?.maps?.places?.Autocomplete) {
    return;
  }
  if (window.google?.maps?.importLibrary) {
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Places library load timeout')), 10000)
    );
    await Promise.race([
      window.google.maps.importLibrary('places'),
      timeout,
    ]);
  }
}

// Context
export const GoogleMapsContext = React.createContext({
  isLoaded: false,
  error: null,
});

export function useGoogleMaps() {
  return React.useContext(GoogleMapsContext);
}
